/* eslint-disable arrow-parens */
/**
 * Creates WebComponent and Manages events,state,rendering,lifecycle.
 */

import { bindEvents } from "./events";
import { getAttrs, immu, objToStyleMap, objectOnChange } from "./utils";
import {
    __$bindInput,
    bindPublicMethodsToContext,
    domConnector,
    filterComputedState,
    filterGlobal$Object,
    filterInitialState,
    filterMethods,
    storeConnector,
    domConnectorTemplate
} from "./component-helpers";

/**
 * Private context included data and functions
 * to exist only in the render function
 */
const renderContext = { __$objToStyleMap: objToStyleMap };

export default function Component(options = {}) {
    const opt: any = {

        /**
         * @type {boolean}
         * if false Web Component will be custom element, else shadow dom
         */
        shadowDOM: false,

        /**
         * @type {string}
         * the element tag name
         */
        tagName: null,

        /**
         * @type {object}
         * local state data
         */
        data: {},

        /**
         * @type {string}
         * The template in component
         */
        template: null,

        /**
         * @type {function}
         * The renderer template function
         */
        renderer: null,
        
        /**
         * @type {{getState: function, subscribe: function }}
         * for global store/state manager
         */
        $store: {
            getState: () => undefined,
            subscribe: () => () => {
            },
        },

        /**
         * @type {function}
         * life cycle method to initialize,
         * it is passed with the current configuration object,
         * initialize life cycle can update the configuration before the actual dom is created
         * @param {object} options - Component configuration object
         */
        initialize(options) {
        },

        /**
         * @type {function}
         * lifecycle
         */
        created() {
        },

        /**
         * @type {function}
         * lifecycle
         */
        updated() {
        },

        /**
         * @type {function}
         * lifecycle
         */
        removed() {
        },

        /**
         * @type {any}
         *
         */
        ...options,
    };

    const store = storeConnector(opt.$store);
    const dom = opt.template === null ?  domConnector(opt.renderer) : domConnectorTemplate(opt.template);
    const methods = filterMethods(opt);
    const initialState = filterInitialState(opt.data);
    const gobal$Object = filterGlobal$Object(opt);

    /**
     * Define and Register the WebComponent
     */
    window.customElements.define(
        opt.tagName.toLowerCase(),
        class extends HTMLElement {
            $root: this | ShadowRoot;

            $eventHooks: { updated: any[]; removed: any[]; } | [];

            _state: any;

            constructor() {
                super();

                /**
                 * @type {Element|ShadowRoot}
                 * use a custom element or shadow dom
                 */
                this.$root = opt.shadowDOM ? this.attachShadow({ mode: "open" }) : this;
                opt.initialize.call(this, opt);
            }

            /**
             * When element is added
             * @returns {void}
             */
            connectedCallback() {
                this.$eventHooks = {
                    updated: [],
                    removed: [],
                };

                this._state = {
                    ...this._state,
                    ...initialState,
                    prop: getAttrs(this, true),
                };

                const computedState = filterComputedState(opt.data, this._state);
                const updateComputedState = state => computedState.forEach(s => s(state));

                const data = objectOnChange(this._state, () => {
                    updateComputedState(this._state);

                    if (dom.render(this.$root, { ...this._state, ...renderContext, ...opt.constants })) {
                        opt.updated.call(this.context);
                        bindEvents(this.$root, this.context);

                        // this._dispatchEventHooks('updated');
                    }
                });

                // context contains methods and properties to work on the element
                this.context = {
                    ...methods,
                    ...gobal$Object,
                    data,
                    ...opt.constants,
                    el: this.$root,
                    prop: this._state.prop,
                    __$bindInput,
                };

                // Bind all the exports methods so it can be accessed in the element
                bindPublicMethodsToContext(this, methods, this.context);

                // Initial setup + first rendering
                updateComputedState(this._state);
                dom.render(this.$root, { ...this._state, ...renderContext, ...opt.constants });
                opt.created.call(this.context);

                // Bind events
                bindEvents(this.$root, this.context);
            }

            context(context: any) {
                throw new Error("Method not implemented.");
            }

            /**
             * When element is removed
             * @returns {void}
             */
            disconnectedCallback() {
                opt.removed.call(this.context);
                this.$eventHooks = [];
            }

            /**
             * Getter data
             * @returns {object} immutable object
             */
            get data() {
                return immu(this._state);
            }
        }
    );
}
